import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import dayjs from "dayjs";
import locales from "../../../../constants/locales";
import HTMLEditor from "../../../../components/html_editor/html_editor";
import { MediaUpload } from "../../../notification/components/media-upload";
import { ConfirmLanguagesDialog } from "../../../notification/components/confirm_languages_dialog";
import SnackbarComp from "../../../notification/components/snackbar";
import { getSignedUrl } from "../../../../services/notifications/notifications";
import { sendEnquiryNotification } from "../../../../services/schemes/schemes";
import { queryClient } from "../../../../providers/query/query";
import RequiredMark from "../../../../components/required_mark/required_mark";

export interface SentNotificationLanguageContent {
  title?: string;
  description?: string;
  tts?: string;
  media_url?: string;
}

export interface SentNotification {
  title: string;
  description: string;
  languageSent: string;
  status: string;
  failureReason?: string | null;
  sentAt: string;
  contents?: Record<string, SentNotificationLanguageContent>;
  imageUrl?: string | null;
  mediaType?: string | null;
}

interface Props {
  enquiryId: string;
  enquiryType: "user" | "family";
  sentNotification?: SentNotification | null;
}

export default function SendEnquiryNotification({
  enquiryId,
  enquiryType,
  sentNotification,
}: Props) {
  const [tabIndex, setTabIndex] = useState(0);
  const [summaryTabIndex, setSummaryTabIndex] = useState(0);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  const [snackbar, setSnackbar] = useState<any>({
    open: false,
    message: "",
    severity: "success",
  });

  const [ttsFilesByLanguage, setTtsFilesByLanguage] = useState<
    Record<string, File>
  >({});
  const [audioFilesByLanguage, setAudioFilesByLanguage] = useState<
    Record<string, File>
  >({});
  const [videoFilesByLanguage, setVideoFilesByLanguage] = useState<
    Record<string, File>
  >({});
  const [separateImageFile, setSeparateImageFile] = useState<
    FileList | undefined
  >(undefined);

  const [sharedData, setSharedData] = useState<SharedFormData>({
    mediaType: null,
    mediaFile: undefined,
    image: undefined,
  });

  const [formData, setFormData] = useState<Record<string, LanguageFormData>>(
    locales.reduce((acc, lang) => {
      acc[lang.code] = { title: "", description: "" };
      return acc;
    }, {} as Record<string, LanguageFormData>)
  );

  const { control, reset, watch } = useForm<LanguageFormData>({
    defaultValues: { title: "", description: "" },
  });

  const { control: sharedControl } = useForm<any>({
    defaultValues: { mediaType: null, mediaFile: "" },
  });

  const currentLang = locales[tabIndex]["code"];
  const watchedTitle = watch("title");
  const watchedDescription = watch("description");

  const handleMediaUploadError = (message: string) => {
    setSnackbar({ open: true, message, severity: "error" });
  };

  const handleMediaTypeChange = (
    type: "image" | "tts" | "audio" | "video" | null | undefined
  ) => {
    setSharedData((prev: any) => ({
      ...prev,
      mediaType: type,
      mediaFile: undefined,
      image: undefined,
    }));
    setSeparateImageFile(undefined);
    if (type !== "audio") setAudioFilesByLanguage({});
    if (type !== "video") setVideoFilesByLanguage({});
    if (type !== "tts") setTtsFilesByLanguage({});
  };

  const handleMediaFileChange = (file: FileList | null) => {
    const mediaType = sharedData.mediaType || "image";
    setSharedData((prev: any) => ({
      ...prev,
      mediaFile: file || undefined,
      image: mediaType === "image" ? file || undefined : prev.image,
    }));
  };

  const handleImageFileChange = (file: FileList | null) => {
    setSeparateImageFile(file || undefined);
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    const newLang = locales[newValue].code;
    setTabIndex(newValue);
    reset(formData[newLang]);
  };

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      [currentLang]: {
        title: watchedTitle,
        description: watchedDescription,
      },
    }));
  }, [watchedTitle, watchedDescription, currentLang]);

  const isEmptyRichText = (html: string = ""): boolean => {
    const plainText = html.replace(/<[^>]+>/g, "").trim();
    return plainText === "";
  };

  const onSubmitAll = () => {
    setIsLoading(true);

    if (!formData["en"]?.title) {
      setIsLoading(false);
      setSnackbar({
        open: true,
        message: `Title cannot be empty for english`,
        severity: "error",
      });
      return;
    }
    if (isEmptyRichText(formData["en"]?.description)) {
      setIsLoading(false);
      setSnackbar({
        open: true,
        message: `Description cannot be empty for english`,
        severity: "error",
      });
      return;
    }

    for (const lang of locales) {
      const title = formData[lang.code]?.title?.trim();
      const description = formData[lang.code]?.description;

      const hasTitle = !!title;
      const hasDescription = !isEmptyRichText(description);

      if (!hasTitle && !hasDescription) continue;

      if (!hasTitle) {
        setIsLoading(false);
        setSnackbar({
          open: true,
          message: `Title cannot be empty for ${lang.name}`,
          severity: "error",
        });
        return;
      } else if (title.length < 10) {
        setIsLoading(false);
        setSnackbar({
          open: true,
          message: `Title is too short for ${lang.name}`,
          severity: "error",
        });
        return;
      } else if (title.length > 60) {
        setIsLoading(false);
        setSnackbar({
          open: true,
          message: `Title is too long for ${lang.name}`,
          severity: "error",
        });
        return;
      }

      if (!hasDescription) {
        setIsLoading(false);
        setSnackbar({
          open: true,
          message: `Description cannot be empty for ${lang.name}`,
          severity: "error",
        });
        return;
      } else if (description.length < 10) {
        setIsLoading(false);
        setSnackbar({
          open: true,
          message: `Description is too short for ${lang.name}`,
          severity: "error",
        });
        return;
      } else if (description.length > 1500) {
        setIsLoading(false);
        setSnackbar({
          open: true,
          message: `Description is too long for ${lang.name}`,
          severity: "error",
        });
        return;
      }
    }

    if (sharedData?.mediaType === "image" && currentLang === "en") {
      const hasImageFile =
        sharedData.mediaFile &&
        (typeof sharedData.mediaFile === "string" ||
          (typeof sharedData.mediaFile !== "string" &&
            sharedData.mediaFile[0]));

      if (!hasImageFile) {
        setIsLoading(false);
        setSnackbar({
          open: true,
          message: `Please select an image file. Image is mandatory for image type notifications.`,
          severity: "error",
        });
        return;
      }
    }

    setIsLoading(false);
    setConfirmDialogOpen(true);
  };

  const handleFinalSubmit = async () => {
    setIsLoading(true);
    try {
      const campaignId = parseInt(
        new Date().valueOf().toString() +
          Math.floor(Math.random() * 100).toString().padEnd(2, "0")
      ).toString();

      const getFileFromMediaFile = (
        mediaFile: FileList | string | undefined
      ): File | null => {
        if (!mediaFile) return null;
        if (typeof mediaFile === "string") return null;
        if (mediaFile instanceof FileList && mediaFile.length > 0) {
          return mediaFile[0];
        }
        return null;
      };

      const getContentType = (file: File): string => {
        if (file.type) return file.type;
        const extension = file.name.split(".").pop()?.toLowerCase();
        if (extension === "jpg" || extension === "jpeg") return "image/jpeg";
        if (extension === "png") return "image/png";
        return "image/jpeg";
      };

      let uploadedMainImageFileName: string | null = null;
      if (sharedData?.mediaType === "image") {
        const imageFile = getFileFromMediaFile(sharedData.mediaFile);
        if (imageFile) {
          const imageExt =
            imageFile.name.split(".").pop()?.toLowerCase() || "jpg";
          const imageFileName = `notification_image.${imageExt}`;
          const contentType = getContentType(imageFile);

          const signedUrlResponse: any = await getSignedUrl({
            fileName: imageFileName,
            bucketType: "notification",
            contentType,
            ObjectId: campaignId,
          });

          const signedUrl =
            signedUrlResponse?.data?.data?.signed_url ||
            signedUrlResponse?.data?.data?.url ||
            signedUrlResponse?.data?.signedUrl ||
            signedUrlResponse?.data?.url;

          if (!signedUrl) {
            throw new Error("Failed to get signed URL for image upload");
          }

          const fileBlob = await imageFile.arrayBuffer();
          const uploadResponse = await fetch(signedUrl, {
            method: "PUT",
            body: fileBlob,
            headers: { "Content-Type": contentType },
          });

          if (!uploadResponse.ok) {
            throw new Error("Failed to upload image file");
          }

          uploadedMainImageFileName = imageFileName;
        } else if (typeof sharedData.mediaFile === "string") {
          const urlParts = sharedData.mediaFile.split("/");
          uploadedMainImageFileName = urlParts[urlParts.length - 1].split(
            "?"
          )[0];
        }
      }

      let uploadedImageFileName: string | null = null;
      if (separateImageFile && separateImageFile[0]) {
        const imageFile = separateImageFile[0];
        const imageExt =
          imageFile.name.split(".").pop()?.toLowerCase() || "jpg";
        const imageFileName = `notification_image.${imageExt}`;

        const signedUrlResponse: any = await getSignedUrl({
          fileName: imageFileName,
          bucketType: "notification",
          contentType: imageFile.type || "image/jpeg",
          ObjectId: campaignId,
        });

        const signedUrl =
          signedUrlResponse?.data?.data?.signed_url ||
          signedUrlResponse?.data?.data?.url ||
          signedUrlResponse?.data?.signedUrl ||
          signedUrlResponse?.data?.url;

        if (!signedUrl) {
          throw new Error("Failed to get signed URL for image upload");
        }

        const fileBlob = await imageFile.arrayBuffer();
        const uploadResponse = await fetch(signedUrl, {
          method: "PUT",
          body: fileBlob,
          headers: { "Content-Type": imageFile.type || "image/jpeg" },
        });

        if (!uploadResponse.ok) {
          throw new Error("Failed to upload image file");
        }

        uploadedImageFileName = imageFileName;
      }

      let ttsUploadedFileNamesByLang: Record<string, string> = {};
      if (sharedData?.mediaType === "tts") {
        if (!Object.keys(ttsFilesByLanguage).length) {
          setIsLoading(false);
          setSnackbar({
            open: true,
            message:
              "Please convert text to speech for at least one language before sending notification.",
            severity: "error",
          });
          return;
        }

        for (const [langCode, file] of Object.entries(ttsFilesByLanguage)) {
          const ext = (file.name.split(".").pop() || "mp3").toLowerCase();
          const safeLang = langCode.toLowerCase();
          const fileName = `tts_audio_${safeLang}.${ext}`;

          const signedUrlResponse: any = await getSignedUrl({
            fileName,
            bucketType: "notification",
            contentType: file.type || "audio/mpeg",
            ObjectId: campaignId,
          });

          const signedUrl =
            signedUrlResponse?.data?.data?.signed_url ||
            signedUrlResponse?.data?.data?.url ||
            signedUrlResponse?.data?.signedUrl ||
            signedUrlResponse?.data?.url;

          if (!signedUrl) {
            throw new Error("Failed to get signed URL for TTS upload");
          }

          const fileBlob = await file.arrayBuffer();
          const uploadResponse = await fetch(signedUrl, {
            method: "PUT",
            body: fileBlob,
            headers: { "Content-Type": file.type || "audio/mpeg" },
          });

          if (!uploadResponse.ok) {
            throw new Error("Failed to upload TTS audio file");
          }

          ttsUploadedFileNamesByLang[langCode] = fileName;
        }
      }

      let audioUploadedFileNamesByLang: Record<string, string> = {};
      if (sharedData?.mediaType === "audio") {
        if (!Object.keys(audioFilesByLanguage).length) {
          setIsLoading(false);
          setSnackbar({
            open: true,
            message:
              "Please upload an audio file for at least one language before sending notification.",
            severity: "error",
          });
          return;
        }
        setIsUploadingMedia(true);

        for (const [langCode, file] of Object.entries(audioFilesByLanguage)) {
          const ext = (file.name.split(".").pop() || "mp3").toLowerCase();
          const safeLang = langCode.toLowerCase();
          const fileName = `audio_${safeLang}.${ext}`;

          const signedUrlResponse: any = await getSignedUrl({
            fileName,
            bucketType: "notification",
            contentType: file.type || "audio/mpeg",
            ObjectId: campaignId,
          });

          const signedUrl =
            signedUrlResponse?.data?.data?.signed_url ||
            signedUrlResponse?.data?.data?.url ||
            signedUrlResponse?.data?.signedUrl ||
            signedUrlResponse?.data?.url;

          if (!signedUrl) {
            throw new Error("Failed to get signed URL for audio upload");
          }

          const fileBlob = await file.arrayBuffer();
          const uploadResponse = await fetch(signedUrl, {
            method: "PUT",
            body: fileBlob,
            headers: { "Content-Type": file.type || "audio/mpeg" },
          });

          if (!uploadResponse.ok) {
            throw new Error("Failed to upload audio file");
          }

          audioUploadedFileNamesByLang[langCode] = fileName;
        }
      }

      let videoUploadedFileNamesByLang: Record<string, string> = {};
      if (sharedData?.mediaType === "video") {
        if (!Object.keys(videoFilesByLanguage).length) {
          setIsLoading(false);
          setSnackbar({
            open: true,
            message:
              "Please upload a video file for at least one language before sending notification.",
            severity: "error",
          });
          return;
        }
        setIsUploadingMedia(true);

        for (const [langCode, file] of Object.entries(videoFilesByLanguage)) {
          const ext = (file.name.split(".").pop() || "mp4").toLowerCase();
          const safeLang = langCode.toLowerCase();
          const fileName = `video_${safeLang}.${ext}`;

          const signedUrlResponse: any = await getSignedUrl({
            fileName,
            bucketType: "notification",
            contentType: file.type || "video/mp4",
            ObjectId: campaignId,
          });

          const signedUrl =
            signedUrlResponse?.data?.data?.signed_url ||
            signedUrlResponse?.data?.data?.url ||
            signedUrlResponse?.data?.signedUrl ||
            signedUrlResponse?.data?.url;

          if (!signedUrl) {
            throw new Error("Failed to get signed URL for video upload");
          }

          const fileBlob = await file.arrayBuffer();
          const uploadResponse = await fetch(signedUrl, {
            method: "PUT",
            body: fileBlob,
            headers: { "Content-Type": file.type || "video/mp4" },
          });

          if (!uploadResponse.ok) {
            throw new Error("Failed to upload video file");
          }

          videoUploadedFileNamesByLang[langCode] = fileName;
        }
      }

      const contentPayload = locales.reduce((acc: any, lang: any) => {
        const langCode = lang.code;
        const { title, description } = formData[langCode] || {};
        const hasContent =
          title?.trim() ||
          (description?.trim() && !isEmptyRichText(description));

        if (hasContent) {
          const contentForLang: any = { title, description };

          if (sharedData?.mediaType === "tts") {
            const audioFileNameForLang = ttsUploadedFileNamesByLang[langCode];
            if (audioFileNameForLang) contentForLang.tts = audioFileNameForLang;
          }
          if (sharedData?.mediaType === "audio") {
            const audioFileNameForLang =
              audioUploadedFileNamesByLang[langCode];
            if (audioFileNameForLang)
              contentForLang.media_url = audioFileNameForLang;
          }
          if (sharedData?.mediaType === "video") {
            const videoFileNameForLang =
              videoUploadedFileNamesByLang[langCode];
            if (videoFileNameForLang)
              contentForLang.media_url = videoFileNameForLang;
          }
          acc[`content_${langCode}`] = contentForLang;
        }
        return acc;
      }, {});

      const formDataPayload = new FormData();
      formDataPayload.append("enquiryId", enquiryId);
      formDataPayload.append("enquiryType", enquiryType);
      formDataPayload.append("contents", JSON.stringify(contentPayload));

      const hasUploadedMedia =
        uploadedMainImageFileName ||
        uploadedImageFileName ||
        Object.keys(ttsUploadedFileNamesByLang).length ||
        Object.keys(audioUploadedFileNamesByLang).length ||
        Object.keys(videoUploadedFileNamesByLang).length;
      if (hasUploadedMedia) {
        formDataPayload.append("ObjectId", campaignId);
      }

      if (sharedData?.mediaType) {
        formDataPayload.append("media_type", sharedData.mediaType);
      }
      if (sharedData?.mediaType === "image") {
        formDataPayload.append("image_url", uploadedMainImageFileName || "");
      } else if (uploadedImageFileName) {
        formDataPayload.append("image_url", uploadedImageFileName);
      }

      setIsUploadingMedia(false);

      await sendEnquiryNotification(formDataPayload);

      queryClient.invalidateQueries({
        queryKey: [enquiryType === "family" ? "familyEnquiry" : "enquiry", enquiryId],
      });

      setIsLoading(false);
      setSnackbar({
        open: true,
        message: "Notification sent successfully",
        severity: "success",
      });
      setConfirmDialogOpen(false);
    } catch (error: any) {
      setIsLoading(false);
      setIsUploadingMedia(false);
      const errorMsg =
        error?.response?.data?.error?.message || "Something went wrong.";
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: "error",
      });
    }
  };

  const filledLanguages = locales
    .filter(
      (lang) =>
        formData[lang.code]?.title.trim() &&
        formData[lang.code]?.description.trim()
    )
    .map((lang) => lang.name);

  if (sentNotification) {
    const languageName =
      locales.find((locale) => locale.code === sentNotification.languageSent)
        ?.name || sentNotification.languageSent;

    const languageEntries = locales
      .map((locale) => {
        const content = sentNotification.contents?.[`content_${locale.code}`];
        if (!content || (!content.title?.trim() && !content.description?.trim())) {
          return null;
        }
        return { code: locale.code, name: locale.name, content };
      })
      .filter(
        (entry): entry is { code: string; name: string; content: SentNotificationLanguageContent } =>
          entry !== null
      );

    const activeEntry = languageEntries[summaryTabIndex] || languageEntries[0];

    return (
      <Card sx={{ width: "100%", borderRadius: 2, mt: 3 }} elevation={1}>
        <CardContent>
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Typography variant="h6" fontWeight="600">
              Notification Sent
            </Typography>
            <Chip label="Success" color="success" size="small" />
          </Box>
          {sentNotification.sentAt && (
            <Typography variant="body2" color="text.secondary" mb={2}>
              Sent on{" "}
              {dayjs(sentNotification.sentAt).format("DD MMM YYYY, hh:mm A")}
              {languageName ? ` · Delivered in ${languageName}` : ""}
            </Typography>
          )}

          {languageEntries.length > 1 ? (
            <Tabs
              value={Math.min(summaryTabIndex, languageEntries.length - 1)}
              onChange={(_, value) => setSummaryTabIndex(value)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{ mb: 2 }}
            >
              {languageEntries.map((entry) => (
                <Tab key={entry.code} label={entry.name} />
              ))}
            </Tabs>
          ) : null}

          {activeEntry ? (
            <>
              <Typography variant="subtitle2" sx={{ color: "#91278F" }} mb={1}>
                Title
              </Typography>
              <Typography mb={2}>{activeEntry.content.title}</Typography>
              <Typography variant="subtitle2" sx={{ color: "#91278F" }} mb={1}>
                Description
              </Typography>
              <HTMLEditor
                value={activeEntry.content.description}
                onChange={() => {}}
                readOnly
              />

              {activeEntry.content.tts && (
                <Box mt={2}>
                  <Typography variant="subtitle2" mb={1}>
                    Audio ({activeEntry.name})
                  </Typography>
                  <audio controls style={{ width: "100%" }} src={activeEntry.content.tts} />
                </Box>
              )}

              {activeEntry.content.media_url && (
                <Box mt={2}>
                  <Typography variant="subtitle2" mb={1}>
                    {sentNotification.mediaType === "video" ? "Video" : "Audio"} ({activeEntry.name})
                  </Typography>
                  {sentNotification.mediaType === "video" ? (
                    <video
                      controls
                      style={{ width: "100%", maxWidth: 500, borderRadius: 8 }}
                      src={activeEntry.content.media_url}
                    />
                  ) : (
                    <audio controls style={{ width: "100%" }} src={activeEntry.content.media_url} />
                  )}
                </Box>
              )}
            </>
          ) : (
            <>
              <Typography variant="subtitle2" sx={{ color: "#91278F" }} mb={1}>
                Title
              </Typography>
              <Typography mb={2}>{sentNotification.title}</Typography>
              <Typography variant="subtitle2" sx={{ color: "#91278F" }} mb={1}>
                Description
              </Typography>
              <HTMLEditor value={sentNotification.description} onChange={() => {}} readOnly />
            </>
          )}

          {sentNotification.imageUrl && (
            <Box mt={2}>
              <Typography variant="subtitle2" mb={1}>
                Image
              </Typography>
              <img
                src={sentNotification.imageUrl}
                alt="Notification"
                style={{ maxWidth: "100%", maxHeight: 300, borderRadius: 8 }}
              />
            </Box>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ width: "100%", borderRadius: 2, mt: 3 }} elevation={1}>
      <CardContent>
        <Typography variant="h6" fontWeight="600" mb={1}>
          Send Notification
        </Typography>

        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          {locales.map((locale) => (
            <Tab key={locale.code} label={locale.name} />
          ))}
        </Tabs>

        <Divider sx={{ mb: 2 }} />

        <Controller
          name="title"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label={
                currentLang === "en" ? (
                  <>
                    Title <RequiredMark />
                  </>
                ) : (
                  "Title"
                )
              }
              margin="normal"
            />
          )}
        />

        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <HTMLEditor
              value={field.value}
              onChange={field.onChange}
              placeholder={`Enter Description ${
                currentLang === "en" ? "*" : ""
              }`}
            />
          )}
        />

        <MediaUpload
          tabIndex={tabIndex}
          viewMode={false}
          mediaType={sharedData.mediaType || null}
          setSharedData={setSharedData}
          mediaFile={sharedData.mediaFile}
          control={sharedControl}
          title={formData[currentLang]?.title || ""}
          language={currentLang}
          onMediaTypeChange={handleMediaTypeChange}
          onMediaFileChange={handleMediaFileChange}
          onUploadSuccess={() => {}}
          onError={handleMediaUploadError}
          onTtsGenerated={(langCode, file) => {
            setTtsFilesByLanguage((prev) => ({ ...prev, [langCode]: file }));
          }}
          onAudioFileChange={(langCode, file) => {
            setAudioFilesByLanguage((prev) => ({ ...prev, [langCode]: file }));
          }}
          onVideoFileChange={(langCode, file) => {
            setVideoFilesByLanguage((prev) => ({ ...prev, [langCode]: file }));
          }}
          onImageFileChange={handleImageFileChange}
          imageFile={separateImageFile}
        />

        {currentLang === "en" && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              alignItems: "center",
              gap: 2,
              mt: 2,
            }}
          >
            {isUploadingMedia && (
              <Typography variant="body2" color="text.secondary">
                Uploading media...
              </Typography>
            )}
            <Button
              disabled={isLoading || isUploadingMedia}
              onClick={onSubmitAll}
              variant="contained"
              color="primary"
              startIcon={
                isUploadingMedia ? (
                  <CircularProgress size={16} color="inherit" />
                ) : null
              }
            >
              {isUploadingMedia ? "Uploading Media..." : "Send Notification"}
            </Button>
          </Box>
        )}
      </CardContent>

      <ConfirmLanguagesDialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={handleFinalSubmit}
        filledLanguages={filledLanguages}
        isLoading={isLoading || isUploadingMedia}
      />

      <SnackbarComp
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </Card>
  );
}
